/**
 * Settlement display-status logic.
 *
 * The persisted `status` is one of "open" | "awaiting_approval" | "locked".
 * The DISPLAY status adds "overdue" — computed from endDate vs now when
 * status is anything but "locked". This is computed at render time, never
 * stored, so a settlement automatically shows as overdue once its endDate
 * passes without anyone having to update it.
 *
 * User-facing labels live in `../i18n`; this file owns logic only.
 */

import { t } from "../i18n";

export type SettlementPersistedStatus = "open" | "awaiting_approval" | "locked";

export type SettlementDisplayStatus = "open" | "awaiting_approval" | "overdue" | "locked";

export interface SettlementStatusInfo {
  status: SettlementDisplayStatus;
  /** Norwegian label for the status badge. */
  label: string;
  /** Tone for the Badge component. */
  tone: "neutral" | "warning" | "danger" | "success" | "accent";
  /** Suggested icon name for the badge. */
  icon: "loader" | "alert-circle" | "check-circle" | "lock" | "calendar";
}

export function getSettlementStatus(args: {
  status: SettlementPersistedStatus;
  endDate: string;
  /** Pass `today` for testability. Defaults to current date. */
  now?: Date;
}): SettlementStatusInfo {
  const { status, endDate } = args;
  const now = args.now ?? new Date();
  const today = now.toISOString().slice(0, 10); // YYYY-MM-DD

  if (status === "locked") {
    return {
      status: "locked",
      label: t.settlementStatus.locked,
      tone: "success",
      icon: "lock",
    };
  }

  // Overdue is computed: end date passed, not yet locked
  if (endDate < today) {
    return {
      status: "overdue",
      label: t.settlementStatus.overdue,
      tone: "danger",
      icon: "alert-circle",
    };
  }

  if (status === "awaiting_approval") {
    return {
      status: "awaiting_approval",
      label: t.settlementStatus.awaitingApproval,
      tone: "warning",
      icon: "loader",
    };
  }

  return {
    status: "open",
    label: t.settlementStatus.open,
    tone: "neutral",
    icon: "calendar",
  };
}

/**
 * Days until the settlement's end date. Negative means past due.
 */
export function daysUntilDue(endDate: string, now = new Date()): number {
  const end = new Date(`${endDate}T23:59:59`);
  const diffMs = end.getTime() - now.getTime();
  return Math.ceil(diffMs / 86_400_000);
}

/**
 * Human-readable "due in X days" or "X days overdue".
 */
export function formatDueRelative(endDate: string, now = new Date()): string {
  const days = daysUntilDue(endDate, now);
  if (days === 0) return t.settlementStatus.dueToday;
  if (days === 1) return t.settlementStatus.dueTomorrow;
  if (days > 0) return t.settlementStatus.dueInDays(days);
  if (days === -1) return t.settlementStatus.overdueOneDay;
  return t.settlementStatus.overdueDays(Math.abs(days));
}
